import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Github, ExternalLink, Plus, X, Sparkles } from "lucide-react";
import { Section, SectionHeading } from "./_shared";

const ProjectCard = ({ p, onOpen }) => {
  const accentRing = p.accent === "violet" ? "from-violet-500/30 to-fuchsia-500/10" : "from-cyan-400/30 to-blue-500/10";
  return (
    <motion.button
      layoutId={`card-${p.id}`}
      onClick={() => onOpen(p.id)}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      className="text-left glass rounded-3xl p-7 md:p-8 relative overflow-hidden group hover:border-cyan-400/30 transition-colors"
      data-testid={`project-card-${p.id}`}
    >
      <div className={`absolute -inset-px rounded-3xl bg-gradient-to-br ${accentRing} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} style={{ zIndex: -1 }} />
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300">
            {p.year || ""} · Project
          </div>
          <motion.h3 layoutId={`title-${p.id}`} className="mt-2 font-display text-3xl md:text-4xl tracking-tight text-white font-medium">
            {p.title}
          </motion.h3>
        </div>
        <div className="w-10 h-10 rounded-full border border-white/10 grid place-items-center text-slate-300 group-hover:text-cyan-300 group-hover:border-cyan-400/40 transition-colors">
          <Plus size={16} />
        </div>
      </div>
      <p className="mt-5 text-slate-400 leading-relaxed">{p.short_description}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {(p.technologies || []).slice(0, 5).map((t) => (
          <span key={t} className="px-2.5 py-1 text-[11px] font-mono rounded-full border border-white/10 bg-white/[0.03] text-slate-300">{t}</span>
        ))}
        {(p.technologies || []).length > 5 && (
          <span className="px-2.5 py-1 text-[11px] font-mono rounded-full text-slate-500">+{(p.technologies || []).length - 5}</span>
        )}
      </div>
    </motion.button>
  );
};

const ProjectModal = ({ project, onClose }) => {
  if (!project) return null;
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="project-modal">
      <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
      <motion.div layoutId={`card-${project.id}`} className="relative w-full max-w-3xl glass rounded-3xl p-7 md:p-10 max-h-[88vh] overflow-y-auto">
        <button onClick={onClose} aria-label="Close" data-testid="project-modal-close" className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/10 grid place-items-center text-slate-300 hover:text-white">
          <X size={16} />
        </button>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300">{project.year} · Case Study</div>
        <motion.h3 layoutId={`title-${project.id}`} className="mt-2 font-display text-4xl md:text-5xl tracking-tight text-white font-medium">{project.title}</motion.h3>
        {project.long_description && <p className="mt-5 text-slate-300 leading-relaxed whitespace-pre-line">{project.long_description}</p>}

        {(project.impact || []).length > 0 && (
          <div className="mt-7">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-3">Impact</div>
            <ul className="space-y-2">
              {(project.impact || []).map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-300 text-[14px] leading-relaxed">
                  <Sparkles size={14} className="mt-1 text-cyan-300 shrink-0" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-7">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-3">Stack</div>
          <div className="flex flex-wrap gap-2">
            {(project.technologies || []).map((t) => (
              <span key={t} className="px-3 py-1.5 text-[12px] font-mono rounded-full border border-white/10 bg-white/[0.04] text-slate-200">{t}</span>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {project.github_url && (
            <a href={project.github_url} target="_blank" rel="noreferrer" data-testid={`project-${project.id}-github`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass text-white text-sm hover:border-cyan-400/40">
              <Github size={15} /> GitHub
            </a>
          )}
          {project.demo_url && (
            <a href={project.demo_url} target="_blank" rel="noreferrer" data-testid={`project-${project.id}-demo`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black text-sm font-medium">
              <ExternalLink size={15} /> Live Demo
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const Projects = ({ items = [] }) => {
  const [openId, setOpenId] = useState(null);
  const active = items.find((p) => p.id === openId) || null;
  return (
    <Section id="projects">
      <SectionHeading index="03" label="Selected Work" title="Projects worth opening." description="Each card expands into a case study with stack, impact and links." testId="projects-heading" />
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((p) => <ProjectCard key={p.id} p={p} onOpen={setOpenId} />)}
      </div>
      <AnimatePresence>
        {active && <ProjectModal project={active} onClose={() => setOpenId(null)} />}
      </AnimatePresence>
    </Section>
  );
};

export default Projects;
